import { useId } from 'react'
import type { Room } from './mascot-game-types'
import sceneStyles from './MascotScene.module.css'
import styles from './JungleDrawing.module.css'

/** Layered vector habitat, using the same scalable scene system as the other pets. */
export function JungleDrawing({
  room,
  sleeping,
  growing,
  decor,
  tint,
}: {
  room: Room
  sleeping: boolean
  growing: boolean
  decor: string
  tint?: string
}) {
  const id = `jungle-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const twilight = sleeping || decor === 'lavender'
  const sky = twilight
    ? '#243d48'
    : decor === 'peach'
      ? '#dab786'
      : decor === 'ocean'
        ? '#95c7c1'
        : '#c5d4a7'
  const foliage = twilight ? '#254c43' : (tint ?? '#3e7253')
  return (
    <svg
      className={`${sceneStyles.drawing} ${styles.jungle}`}
      viewBox="0 0 960 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      data-jungle-room={room}
    >
      <defs>
        <linearGradient id={`${id}-sky`} x2="0" y2="1">
          <stop stopColor={sky} />
          <stop offset="1" stopColor={twilight ? '#547061' : '#e1dfac'} />
        </linearGradient>
        <linearGradient id={`${id}-water`} x2="0" y2="1">
          <stop stopColor={twilight ? '#507777' : '#8cbab1'} />
          <stop offset="1" stopColor={twilight ? '#315559' : '#4e938e'} />
        </linearGradient>
        <linearGradient id={`${id}-trunk`}>
          <stop stopColor="#3b4630" />
          <stop offset=".5" stopColor="#68714b" />
          <stop offset="1" stopColor="#424c32" />
        </linearGradient>
        <radialGradient id={`${id}-light`}>
          <stop stopColor="#fff6ca" stopOpacity=".52" />
          <stop offset="1" stopColor="#fff6ca" stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d="M0 0h960v600H0Z" fill={`url(#${id}-sky)`} />
      <circle
        cx="560"
        cy="142"
        r={twilight ? 30 : 55}
        fill={twilight ? '#efe8b1' : '#fff0b0'}
        opacity=".8"
      />
      <g fill={foliage} opacity=".25">
        <path d="M0 240Q120 140 220 215T435 194T690 213T960 165v340H0Z" />
        {[65, 198, 330, 650, 780, 920].map((x, i) => (
          <path key={x} d={`M${x} 95q${i % 2 ? 15 : -18} 158 0 345h18q-15-192 8-345Z`} />
        ))}
      </g>
      <path
        d="M0 373q134-89 272-22t303 2q190-86 385-7v254H0Z"
        fill={twilight ? '#35584b' : '#719667'}
      />
      <path
        d="M0 453q192-105 390-30t570-41v218H0Z"
        fill={twilight ? '#29483c' : '#4d7851'}
      />
      <path
        d="M960 324q-215 7-178 85t-161 65q-113-5-197 126h536Z"
        fill={`url(#${id}-water)`}
      />
      <g
        className={styles.water}
        fill="none"
        stroke="#d9eed3"
        strokeWidth="2"
        opacity=".45"
      >
        <path d="M797 370h72m-89 35h50m-112 80h103m-180 41h124m-183 40h161" />
      </g>
      <ellipse
        cx="470"
        cy="496"
        rx="220"
        ry="72"
        fill={twilight ? '#597052' : '#a1ad70'}
      />
      <ellipse
        cx="465"
        cy="490"
        rx="174"
        ry="40"
        fill={twilight ? '#677953' : '#bfc38a'}
        opacity=".65"
      />
      {!twilight && (
        <g className={styles.light}>
          <path d="m431 0 91 0 191 513-325-10Z" fill="#fff2b9" opacity=".09" />
          <ellipse cx="480" cy="337" rx="300" ry="238" fill={`url(#${id}-light)`} />
        </g>
      )}
      <g fill={`url(#${id}-trunk)`}>
        <path d="M70 0h83q-48 217-28 396l51 90-68-25-67 28 38-85Q85 183 70 0Z" />
        <path d="M813 0h70q-10 204 21 377l37 62-65-15-43 25 10-74Q826 153 813 0Z" />
        <path d="M103 140Q26 89 0 39v50q32 48 103 78Zm32 87q87-39 117-130l-20-11q-37 80-99 97Z" />
      </g>
      <g
        fill="none"
        stroke="#8b9b59"
        strokeWidth="5"
        strokeLinecap="round"
        className={styles.vines}
      >
        <path d="M233-10q-35 90 3 155t-8 95M725-10q37 110-2 215M915 0q-45 94-7 156" />
      </g>
      <g fill={foliage} className={styles.canopy}>
        <path d="M0 0h960v46q-62 70-132 19-81 67-154-6-65 39-121-10-77 54-135 10-65 67-149 11-77 71-147 8Q36 147 0 104Z" />
        <ellipse cx="45" cy="119" rx="95" ry="62" />
        <ellipse cx="913" cy="95" rx="97" ry="70" />
        <path d="M0 239q54-6 96 58Q18 328 0 279Zm960-43q-70-4-104 54 78 29 104-2Z" />
      </g>
      <g fill={twilight ? '#527453' : '#8da75c'} opacity=".85">
        <path d="M44 50Q77 6 118 26 83 68 44 50Zm128-27q50-25 79 11-55 25-79-11Zm562 0q31-14 54 18-34 14-54-18Zm100 19q45-31 73 8-45 31-73-8Z" />
      </g>
      {room === 'bedroom' && (
        <g data-jungle-shelter>
          <path d="M632 439q-9-112 92-133 98-13 148 126Z" fill="#69735c" />
          <path d="M669 433q-4-89 64-99 59-2 88 97Z" fill="#2f4035" />
          <path d="M666 432q81-27 164 0l-16 16H681Z" fill="#8c9d5e" />
          <path
            d="M664 338q40-48 91-26"
            stroke="#93a16b"
            strokeWidth="9"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      )}
      {room === 'bath' && (
        <g data-jungle-waterfall>
          <path d="M665 257q39-51 89-19l37 114-147 9Z" fill="#687963" />
          <path d="M695 262h37l15 137-69-4Z" fill="#b0d7cc" opacity=".85" />
          <g className={styles.water} fill="none" stroke="#e3f5db" strokeWidth="4">
            <path d="m706 278-9 97m23-80 9 83" />
            <ellipse cx="712" cy="403" rx="57" ry="12" />
          </g>
        </g>
      )}
      {room === 'kitchen' && (
        <g data-jungle-feeding>
          <ellipse cx="722" cy="452" rx="75" ry="23" fill="#77816a" />
          <ellipse cx="722" cy="443" rx="71" ry="20" fill="#a8ae8d" />
          <path
            d="M690 439q-15-20 4-27 11-6 25 6 15-5 23 8 10 18-17 19Z"
            fill="#be7058"
            stroke="#efd3ae"
            strokeWidth="4"
          />
          <path d="M700 424q15 11 28 8" stroke="#ecc29c" strokeWidth="3" fill="none" />
        </g>
      )}
      {room === 'garden' && (
        <g
          data-jungle-sapling
          transform={`translate(725 433) scale(${growing ? 1.4 : 0.85})`}
        >
          <ellipse cy="7" rx="36" ry="11" fill="#425b38" />
          <path d="M0 4v-67" stroke="#8f8152" strokeWidth="6" />
          <path
            d="M0-24q-55 0-43-36 34-9 43 36M0-43q42 0 37-29-30-9-37 29"
            fill="#8fb86b"
          />
        </g>
      )}
      <g
        className={styles.ferns}
        fill={twilight ? '#284f3e' : '#315e41'}
        stroke={twilight ? '#597953' : '#719657'}
        strokeWidth="2"
      >
        <path d="M0 600V423q55-7 72 46l-40-7 52 43-48-7 50 43-49-9 69 68ZM960 600V436q-59-3-87 43l46-4-66 43 52-4-62 45 64-10-71 51Z" />
        <path d="M125 600q-67-78-50-105 63-13 80 54-17-86 22-116 53 26 13 107 58-59 97-25-8 43-92 85Z" />
        <path d="M764 600q-54-42-32-70 46-9 58 43-1-80 36-99 34 31 1 80 62-35 79-1-23 40-90 47Z" />
      </g>
      <g className={styles.fireflies} fill={twilight ? '#e9eea5' : '#f4e8b1'}>
        {[
          [292, 322],
          [627, 268],
          [365, 419],
          [689, 187],
          [569, 384],
        ].map(([x, y], i) => (
          <circle key={x} cx={x} cy={y} r={i % 2 ? 2 : 3} />
        ))}
      </g>
    </svg>
  )
}
